'use client';

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Dictionary } from '@/dictionaries';
import { AffiliateService } from '@/services/affiliate';
import { AffiliateFamily, AffiliateGender, AffiliateProduct } from '@/types/affiliate';
import { dedupeProducts, resolveAffiliateGender } from '@/utils/affiliate';
import { useAuth } from '@/hooks/use-auth';
import { useUserProfile } from '@/hooks/use-user';
import { useIsSpain } from '@/hooks/use-is-spain';
import ProductCard from '@/components/affiliate/ProductCard';
import ProductStrip from '@/components/affiliate/ProductStrip';
import {
  KIDS_AUDIENCES,
  MAX_PRODUCTS_PER_REQUEST,
  PRODUCTS_PER_FAMILY_ROW,
  PRODUCTS_PER_SINGLE_FAMILY,
  PRODUCT_AUDIENCES,
  PRODUCT_FAMILIES,
  PRODUCT_POOL_BATCHES,
  PRODUCT_SPORT_FALLBACK_NAMES,
  PRODUCT_SPORT_IDS,
  ProductAudience,
} from '@/components/affiliate/products.constants';

const affiliateService = AffiliateService.getInstance();

/** `all` = sin filtro (el endpoint sirve entonces el pool genérico, el más profundo). */
type SportFilter = number | 'all';
type FamilyFilter = AffiliateFamily | 'all';
/** El público puede venir del perfil, y entonces vale también `boy`/`girl`. */
type AudienceFilter = AffiliateGender | 'all';

interface RowState {
  products: AffiliateProduct[];
  loading: boolean;
}

interface LoadFamilyOptions {
  family: AffiliateFamily;
  sportId?: number;
  gender?: AffiliateGender;
  limit: number;
  /** Tandas del pool a pedir de entrada (ver PRODUCT_POOL_BATCHES). */
  batches: number;
  signal: AbortSignal;
}

/**
 * Trae los productos de una familia. El endpoint devuelve como mucho 24 por
 * llamada y no ofrece paginación, pero baraja el pool de la familia (~60
 * modelos) en cada una, así que para pintar más de 24 se piden varias tandas y
 * se une lo que traigan.
 *
 * Las tandas van EN PARALELO: cuando se busca más de lo que cabe en una
 * respuesta, ninguna puede completar la sección por sí sola, así que
 * encadenarlas solo sumaría un viaje al servidor por tanda.
 */
const loadFamilyProducts = async ({
  family,
  sportId,
  gender,
  limit,
  batches,
  signal,
}: LoadFamilyOptions): Promise<AffiliateProduct[]> => {
  const byId = new Map<string, AffiliateProduct>();

  const collect = (items: AffiliateProduct[]) => {
    for (const product of items) {
      byId.set(product.productId || product.clickUrl, product);
    }
  };

  // La cadena de fallback del endpoint (subdeporte → deporte → genérico) puede
  // devolver productos de otra familia cuando no hay inventario del filtro
  // exacto. Los descartamos: es preferible ocultar la sección que rotular
  // "Zapatillas" una fila de camisetas.
  const usable = () =>
    dedupeProducts([...byId.values()].filter((product) => product.family === family));

  // `variant` viaja como `_`: el endpoint lo ignora, solo sirve para que la URL
  // cambie y la respuesta no salga de caché (ni de la nuestra ni de la del
  // navegador), con lo que el pool se baraja otra vez y la tanda trae otro
  // subconjunto. La tanda 0 va sin `_` porque no tiene nada que invalidar.
  const fetchBatch = (variant: number) =>
    affiliateService.getBanners({
      sportId,
      family,
      gender,
      ctx: 'products',
      n: MAX_PRODUCTS_PER_REQUEST,
      variant: variant || undefined,
      signal,
    });

  const responses = await Promise.all(
    Array.from({ length: batches }, (_, variant) => fetchBatch(variant))
  );
  for (const { data } of responses) {
    if (data) collect(data);
  }

  // Una tanda de margen si el solape entre tandas y el descarte de variantes de
  // talla han dejado la sección corta. Con el pool agotado no traerá nada nuevo
  // y `usable()` se queda igual, así que no hace falta comprobarlo.
  if (usable().length < limit) {
    const { data } = await fetchBatch(batches);
    if (data) collect(data);
  }

  return usable().slice(0, limit);
};

const parseSportFilter = (value: string | null): SportFilter => {
  const sportId = Number(value);
  return PRODUCT_SPORT_IDS.some((id) => id === sportId) ? sportId : 'all';
};

const parseFamilyFilter = (value: string | null): FamilyFilter =>
  PRODUCT_FAMILIES.find((family) => family === value) ?? 'all';

const parseAudienceFilter = (value: string | null): AudienceFilter | null => {
  const normalized = (value ?? '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'all') return 'all';
  if (PRODUCT_AUDIENCES.some((audience) => audience === normalized)) {
    return normalized as ProductAudience;
  }
  // `boy`/`girl` no se ofrecen en la UI pero pueden llegar en un enlace
  // compartido: se aceptan y se muestran bajo la opción de niños.
  if (KIDS_AUDIENCES.some((audience) => audience === normalized)) {
    return normalized as AffiliateGender;
  }
  return null;
};

const SkeletonCard = () => (
  <div className="animate-pulse rounded-lg border border-border bg-card p-3">
    {/* Misma proporción que el marco real de ProductCard: si no, la fila salta
        de alto al reemplazar el esqueleto por las tarjetas. */}
    <div className="mb-2.5 aspect-[4/3] w-full rounded-md bg-muted" />
    <div className="mb-1.5 h-2 w-1/3 rounded bg-muted" />
    <div className="mb-1 h-3 w-full rounded bg-muted" />
    <div className="h-3 w-2/3 rounded bg-muted" />
  </div>
);

interface FilterGroupProps {
  label: string;
  /** Separación entre opciones: las pastillas piden más aire que los chips. */
  gapClass?: string;
  /** Clases del contenedor, para repartir el ancho entre grupos hermanos. */
  className?: string;
  children: ReactNode;
}

/**
 * Grupo de filtro: el título encima de sus opciones, y las opciones envolviendo
 * cuando no caben en una línea (en móvil, siempre). Sin scroll horizontal: una
 * fila que se desliza esconde opciones sin avisar de que están ahí.
 */
const FilterGroup = ({
  label,
  gapClass = 'gap-2',
  className = '',
  children,
}: FilterGroupProps) => (
  <div className={className}>
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <div className={`flex flex-wrap items-center ${gapClass}`}>{children}</div>
  </div>
);

interface ProductsPageClientProps {
  lang: string;
  dictionary: Dictionary;
}

export default function ProductsPageClient({ lang, dictionary }: ProductsPageClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { userProfile, loading: loadingUser } = useUserProfile();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isSpain = useIsSpain();

  // Deporte y tipo viven en la URL: así la página se puede enlazar ya filtrada
  // (los "Ver todos" de las secciones de eventos pasan el deporte del listado).
  const sportFilter = parseSportFilter(searchParams.get('sport'));
  const familyFilter = parseFamilyFilter(searchParams.get('type'));

  // El público arranca del perfil del usuario, así que hasta que se sabe (o se
  // confirma que no hay sesión) se queda en null y no se pide nada.
  const [audience, setAudience] = useState<AudienceFilter | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const t = dictionary.products;

  const familyLabels = useMemo<Record<AffiliateFamily, string>>(
    () => ({
      footwear: t.family_footwear,
      top: t.family_top,
      bottom: t.family_bottom,
      outerwear: t.family_outerwear,
      accessory: t.family_accessory,
      nutrition: t.family_nutrition,
      equipment: t.family_equipment,
    }),
    [t]
  );

  const audienceLabels = useMemo<Record<ProductAudience, string>>(
    () => ({
      male: t.audience_male,
      female: t.audience_female,
      kids: t.audience_kids,
    }),
    [t]
  );

  // Los nombres de deporte salen del catálogo de eventos (mismos ids), así ya
  // vienen traducidos a los 7 idiomas.
  const sportNames = dictionary.sportsTranslations?.names as unknown as
    | Record<string, string>
    | undefined;

  const sportLabel = useCallback(
    (sportId: number) =>
      sportNames?.[String(sportId)] || PRODUCT_SPORT_FALLBACK_NAMES[sportId] || String(sportId),
    [sportNames]
  );

  const updateFilter = useCallback(
    (key: 'sport' | 'type' | 'who', value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    },
    [pathname, router, searchParams]
  );

  const selectAudience = (value: AudienceFilter) => {
    setAudience(value);
    updateFilter('who', value === 'all' ? null : value);
  };

  // Público inicial: el de la URL si viene, y si no el del perfil (los menores
  // se clasifican como boy/girl/kids). Solo se resuelve una vez: después manda
  // lo que elija el usuario.
  useEffect(() => {
    if (audience !== null || authLoading || loadingUser) return;

    const fromUrl = parseAudienceFilter(searchParams.get('who'));
    if (fromUrl) {
      setAudience(fromUrl);
      return;
    }

    const fromProfile = isAuthenticated
      ? resolveAffiliateGender(userProfile?.gender, userProfile?.birthDate)
      : undefined;
    setAudience(fromProfile ?? 'all');
  }, [
    audience,
    authLoading,
    loadingUser,
    isAuthenticated,
    userProfile?.gender,
    userProfile?.birthDate,
    searchParams,
  ]);

  // Una petición por familia visible, en paralelo, y cada sección se pinta en
  // cuanto llega la suya (no esperamos a la más lenta).
  useEffect(() => {
    if (audience === null) return;

    const abortController = new AbortController();
    const families = familyFilter === 'all' ? PRODUCT_FAMILIES : [familyFilter];
    // Con todas las familias a la vez, los 12 de cada fila caben en una sola
    // respuesta del endpoint; filtrando por una, se piden varias tandas del pool
    // y se unen para poder pintar bastantes más de las 24 del tope por llamada.
    const { limit, batches } =
      familyFilter === 'all'
        ? { limit: PRODUCTS_PER_FAMILY_ROW, batches: 1 }
        : { limit: PRODUCTS_PER_SINGLE_FAMILY, batches: PRODUCT_POOL_BATCHES };

    setRows(
      Object.fromEntries(families.map((family) => [family, { products: [], loading: true }]))
    );

    families.forEach(async (family) => {
      const products = await loadFamilyProducts({
        family,
        sportId: sportFilter === 'all' ? undefined : sportFilter,
        gender: audience === 'all' ? undefined : audience,
        limit,
        batches,
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) return;
      setRows((previous) => ({ ...previous, [family]: { products, loading: false } }));
    });

    return () => abortController.abort();
  }, [audience, familyFilter, sportFilter]);

  // Todas las familias = una muestra de cada una en tira deslizable; una familia
  // filtrada = su parrilla completa, con la misma tarjeta y las mismas columnas.
  const showAllFamilies = familyFilter === 'all';
  const visibleFamilies = showAllFamilies ? PRODUCT_FAMILIES : [familyFilter];
  const loadedRows = visibleFamilies.filter((family) => rows[family] && !rows[family].loading);
  const isEmpty =
    loadedRows.length === visibleFamilies.length &&
    loadedRows.every((family) => rows[family].products.length === 0);

  const pillClass = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-4 py-1.5 text-[13px] transition-colors ${
      active
        ? 'border-foreground bg-foreground font-semibold text-background'
        : 'border-border bg-card text-muted-foreground hover:border-muted-foreground hover:text-foreground'
    }`;

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-md px-3 py-1 text-[13px] transition-colors ${
      active
        ? 'bg-primary/10 font-semibold text-primary'
        : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
    }`;

  return (
    <>
      {/* Filtros. Deporte arriba (la decisión principal), y debajo público y
          tipo de producto, que afinan. */}
      <div className="mb-6 space-y-4 border-b border-border pb-4">
        <FilterGroup label={t.filter_sport}>
          <button
            type="button"
            className={pillClass(sportFilter === 'all')}
            onClick={() => updateFilter('sport', null)}
          >
            {t.all_sports}
          </button>
          {PRODUCT_SPORT_IDS.map((sportId) => (
            <button
              key={sportId}
              type="button"
              className={pillClass(sportFilter === sportId)}
              onClick={() => updateFilter('sport', String(sportId))}
            >
              {sportLabel(sportId)}
            </button>
          ))}
        </FilterGroup>

        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-8">
          <FilterGroup label={t.filter_type} gapClass="gap-x-1 gap-y-1.5">
            <button
              type="button"
              className={chipClass(familyFilter === 'all')}
              onClick={() => updateFilter('type', null)}
            >
              {t.all_types}
            </button>
            {PRODUCT_FAMILIES.map((family) => (
              <button
                key={family}
                type="button"
                className={chipClass(familyFilter === family)}
                onClick={() => updateFilter('type', family)}
              >
                {familyLabels[family]}
              </button>
            ))}
          </FilterGroup>

          <FilterGroup
            label={t.filter_audience}
            gapClass="gap-x-1 gap-y-1.5"
            className="sm:shrink-0"
          >
            <button
              type="button"
              className={chipClass(audience === 'all')}
              onClick={() => selectAudience('all')}
            >
              {t.all_audiences}
            </button>
            {PRODUCT_AUDIENCES.map((option) => {
              // La opción de niños queda activa también con boy/girl, que es lo
              // que puede resolver el perfil del usuario.
              const active =
                option === 'kids'
                  ? audience !== null &&
                    audience !== 'all' &&
                    KIDS_AUDIENCES.includes(audience)
                  : audience === option;

              return (
                <button
                  key={option}
                  type="button"
                  className={chipClass(active)}
                  onClick={() => selectAudience(option)}
                >
                  {audienceLabels[option]}
                </button>
              );
            })}
          </FilterGroup>
        </div>
      </div>

      {visibleFamilies.map((family) => {
        // Sin entrada todavía (esperando a saber el público del perfil) se
        // pinta el esqueleto: la página no se queda en blanco.
        const row = rows[family] ?? { products: [], loading: true };

        // Sección vacía = sección que no se pinta: un hueco con "sin
        // resultados" por familia resta más de lo que informa.
        if (!row.loading && row.products.length === 0) return null;

        // Solo los 6 que se ven: en la tira, el resto llega al deslizar.
        const cards = row.loading
          ? Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />)
          : row.products.map((product) => (
              <ProductCard
                key={product.productId}
                product={product}
                lang={lang}
                seeProductLabel={dictionary.affiliate.see_product}
                amazonAvailableLabel={dictionary.affiliate.available_at_amazon}
                isSpain={isSpain}
                ctx="products"
              />
            ));

        return (
          <section key={family} className="mb-9">
            <div className="mb-3 flex items-baseline gap-3">
              <h2 className="text-xl font-bold sm:text-2xl">{familyLabels[family]}</h2>
              {!row.loading && (
                <span className="text-[13px] text-muted-foreground">
                  {row.products.length} {t.models}
                </span>
              )}
              {/* Cada sección es solo una muestra del tipo, así que se ofrece el
                  salto a su parrilla completa (= filtrar por ese tipo). */}
              {showAllFamilies && (
                <button
                  type="button"
                  onClick={() => updateFilter('type', family)}
                  className="ml-auto shrink-0 text-[13px] font-semibold text-primary hover:underline"
                >
                  {dictionary.affiliate.see_all}
                </button>
              )}
            </div>

            {showAllFamilies ? (
              <ProductStrip prevLabel={t.scroll_prev} nextLabel={t.scroll_next}>
                {cards}
              </ProductStrip>
            ) : (
              <div className="product-track product-grid">{cards}</div>
            )}
          </section>
        );
      })}

      {isEmpty && (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">{t.empty}</p>
          <button
            type="button"
            className="mt-3 text-sm font-semibold text-primary hover:underline"
            onClick={() => {
              setAudience('all');
              router.replace(pathname, { scroll: false });
            }}
          >
            {t.clear_filters}
          </button>
        </div>
      )}

      {/* Letra pequeña al pie: el precio del feed es orientativo (se refresca a
          diario, pero la tienda manda) y la nota de afiliación. */}
      <div className="mt-6 space-y-1.5 border-t border-border pt-4 text-[12px] leading-relaxed text-muted-foreground">
        <p>{t.price_note}</p>
        <p>{dictionary.affiliate.disclosure}</p>
      </div>
    </>
  );
}
